/**
 * GPU Transform Manager Composable
 * 
 * Manages GPU-accelerated transformations for elements using transform3d and scale3d.
 * Provides hardware-accelerated animations without triggering reflow/repaint.
 * 
 * Features:
 * - Uses translate3d instead of left/top for position changes
 * - Uses scale3d instead of width/height for size changes
 * - Always includes translateZ(0) to force GPU layer creation
 * - Calculates scale factor from cached measurements
 * - Sets transform-origin appropriately
 */

import { ref } from 'vue';

export interface TransformState {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  baseWidth: number;
  baseHeight: number;
}

export interface TransformOptions {
  /** Transform origin (e.g., 'left center', 'center center') */
  transformOrigin?: string;
  /** Enable logging for debugging */
  enableLogging?: boolean;
}

export interface GPUTransformManager {
  /** Apply GPU-accelerated transform to element */
  applyTransform: (
    element: HTMLElement,
    x: number,
    y: number,
    width?: number,
    height?: number
  ) => void;
  
  /** Set base dimensions for scale calculations */
  setBaseDimensions: (width: number, height: number) => void;
  
  /** Get current transform state */
  getTransformState: () => TransformState;
  
  /** Reset transform to initial state */
  reset: () => void;
  
  /** Check if GPU acceleration is available */
  isGPUAvailable: () => boolean;
}

/**
 * Create GPU Transform Manager
 * 
 * @param options - Transform options
 * @returns GPU Transform Manager instance
 */
export function useGPUTransform(options: TransformOptions = {}): GPUTransformManager {
  const {
    transformOrigin = 'left center',
    enableLogging = false
  } = options;

  // Transform state
  const transformState = ref<TransformState>({
    x: 0,
    y: 0,
    scaleX: 1,
    scaleY: 1,
    baseWidth: 0,
    baseHeight: 0
  });

  // GPU availability flag
  const gpuAvailable = ref(true);

  /**
   * Log message if logging enabled
   */
  function log(message: string, ...args: any[]) {
    if (enableLogging && !process.env.SERVER) {
      console.log(`[GPUTransform] ${message}`, ...args);
    }
  }

  /**
   * Check if GPU acceleration is supported
   */
  function checkGPUSupport(): boolean {
    try {
      const testEl = document.createElement('div');
      testEl.style.transform = 'translate3d(0, 0, 0)';
      return testEl.style.transform !== '';
    } catch (error) {
      log('GPU acceleration check failed', error);
      return false;
    }
  }

  /**
   * Apply GPU-accelerated transform to element
   * 
   * Uses translate3d for position and scale3d for size changes.
   * Always includes translateZ(0) to force GPU layer creation.
   * 
   * @param element - Target element
   * @param x - X position
   * @param y - Y position
   * @param width - Target width (optional, uses scale if provided)
   * @param height - Target height (optional, uses scale if provided)
   */
  function applyTransform(
    element: HTMLElement,
    x: number,
    y: number,
    width?: number,
    height?: number
  ): void {
    try {
      // Update transform state
      transformState.value.x = x;
      transformState.value.y = y;

      // Calculate scale factors if dimensions provided
      let scaleX = 1;
      let scaleY = 1;

      if (width !== undefined && transformState.value.baseWidth > 0) {
        scaleX = width / transformState.value.baseWidth;
        transformState.value.scaleX = scaleX;
      }

      if (height !== undefined && transformState.value.baseHeight > 0) {
        scaleY = height / transformState.value.baseHeight;
        transformState.value.scaleY = scaleY;
      }

      // Set transform-origin
      element.style.transformOrigin = transformOrigin;

      // GPU-accelerated transform with animation
      // Always include translateZ(0) to force GPU layer creation
      const transform = `translate3d(${x}px, ${y}px, 0) scale3d(${scaleX}, ${scaleY}, 1)`;
      element.style.transform = transform;
      
      log('Applied GPU transform', { x, y, scaleX, scaleY, transform });

      gpuAvailable.value = true;
    } catch (error) {
      // Fallback to CPU rendering
      log('GPU transform failed, falling back to CPU rendering', error);
      gpuAvailable.value = false;
      
      try {
        // CPU fallback - use left/top and width/height
        element.style.left = `${x}px`;
        element.style.top = `${y}px`;
        
        if (width !== undefined) {
          element.style.width = `${width}px`;
        }
        
        if (height !== undefined) {
          element.style.height = `${height}px`;
        }
        
        log('Applied CPU fallback', { x, y, width, height });
      } catch (fallbackError) {
        if (!process.env.SERVER) {
          console.error('[GPUTransform] CPU fallback also failed', fallbackError);
        }
      }
    }
  }

  /**
   * Set base dimensions for scale calculations
   * 
   * These dimensions are used as the reference for calculating scale factors.
   * Should be called once with the initial/natural dimensions of the element.
   * 
   * @param width - Base width
   * @param height - Base height
   */
  function setBaseDimensions(width: number, height: number): void {
    transformState.value.baseWidth = width;
    transformState.value.baseHeight = height;
    log('Set base dimensions', { width, height });
  }

  /**
   * Get current transform state
   */
  function getTransformState(): TransformState {
    return { ...transformState.value };
  }

  /**
   * Reset transform to initial state
   */
  function reset(): void {
    transformState.value = {
      x: 0,
      y: 0,
      scaleX: 1,
      scaleY: 1,
      baseWidth: transformState.value.baseWidth,
      baseHeight: transformState.value.baseHeight
    };
    log('Reset transform state');
  }

  /**
   * Check if GPU acceleration is available
   */
  function isGPUAvailable(): boolean {
    return gpuAvailable.value && checkGPUSupport();
  }

  // Initialize GPU support check
  gpuAvailable.value = checkGPUSupport();

  return {
    applyTransform,
    setBaseDimensions,
    getTransformState,
    reset,
    isGPUAvailable
  };
}
